import { Dialog } from "primereact/dialog";
import React, { useEffect, useRef, useState } from "react";
import DataListModalSearch from "./DataListModal/DataListModalSearch";
import DataListModalClose from "./DataListModal/DataListModalClose";
import CustomTableLoader from "./CustomTableLoader";
import DataException from "./DataException";
import { roleCreateNewAPI, roleDataGetAPI, roleDeleteAPI, roleUpdateAPI } from "../Api/ContactApi";
import { mapRoles, Role } from "../Controllers/Types/UserRoleInterface";
import { InputText } from "primereact/inputtext";
import { Ripple } from "primereact/ripple";
import { confirmDialog } from "primereact/confirmdialog";
import { checkText } from "../Utils/Validator";
import { Toast } from "primereact/toast";

interface RoleSelectionModal {
    isOpen: boolean;
    canEdit: boolean;
    onClose: () => void;
    onSelected: (role: Role) => void;
}

const RoleSelectionModal: React.FC<RoleSelectionModal> = ({
    isOpen,
    canEdit,
    onClose,
    onSelected
}) => {
    const [loadingRole, setLoadingRole] = useState<boolean>(false);
    const [loadingAddRole, setloadingAddRole] = useState<boolean>(false);
    const [customDataState, setCustomDataState] = useState<string>('Add');
    const [errorRole, setErrorRole] = useState<string>('');
    const [searchKey, setSearchKey] = useState<string>('');
    const [roleName, setRoleName] = useState<string>('');
    const [roleNameError, setRoleNameError] = useState<string>('');
    const [roleList, setRoleList] = useState<Role[]>([]);
    const [filterRoleList, setFilterRoleList] = useState<Role[]>([]);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [selectedRoleEdit, setSelectedRoleEdit] = useState<Role | null>(null);
    const [roleUpdateHasChanges, setRoleUpdateHasChanges] = useState<boolean>(false);
    const [roleDeleteLoadingId, setRoleDeleteLoadingId] = useState<number | null>(null);
    const toast = useRef<Toast>(null);


    const roleDataGet = async () => {
        setLoadingRole(true);
        try {
            const data = await roleDataGetAPI(localStorage.getItem('token') || '');
            if (data.status) {
                setRoleList(mapRoles(data.data || []) || []);
            }
        } catch (error) {
            setErrorRole(error ? 'Error fetching roles data.' : '');
        } finally {
            setLoadingRole(false);
        }
    }

    useEffect(() => {
        roleDataGet();
    }, []);

    useEffect(() => {
        if (searchKey && searchKey !== "") {
            const filter = roleList.filter((e) => e.role_name.toLowerCase().includes(searchKey.toLowerCase()))
            setFilterRoleList(filter);
        } else {
            setFilterRoleList(roleList);
        }

    }, [roleList, searchKey]);


    function checkCredentialsRoleCreate(roleName: string): boolean {
        const isRoleNameValid = checkText(roleName, true, setRoleNameError);
        return isRoleNameValid;
    }

    // For create custom role
    const handleCreateRole = async (e: React.FormEvent) => {
        e.preventDefault();
        if (checkCredentialsRoleCreate(roleName)) {
            const data = await roleCreateNewAPI(roleName);
            if (!data.status) { // false
                if (data.statusCode === 422) {
                    Object.entries(data.errors ?? []).map(([key, value], index) => (
                        setRoleNameError(value[0])
                    ))
                }
            } else {
                /* clear data */
                setRoleName('');
                /* call call data function */
                roleDataGet();

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Role created successfully.',
                        life: 3000
                    });
                }
            }
        }


    };


    const handleUpdateRole = async (e: React.FormEvent) => {
        e.preventDefault();
        if (checkCredentialsRoleCreate(roleName) && (selectedRoleEdit && selectedRoleEdit !== null)) {
            const data = await roleUpdateAPI(roleName, selectedRoleEdit.id);
            if (!data.status) { // false
                if (data.statusCode === 422) {
                    Object.entries(data.errors ?? []).map(([key, value], index) => (
                        setRoleNameError(value[0])
                    ))
                }
            } else {
                /* clear data */
                setRoleName('');
                setSelectedRoleEdit(null);
                /* call call data function */
                roleDataGet();

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Role updated successfully.',
                        life: 3000
                    });
                }
            }
        }


    };


    const handleSelectRole = async (role: Role) => {
        setSelectedRole(role);
        onSelected(role);
        onClose();
    }


    const handleEditRole = async (role: Role) => {
        setSelectedRoleEdit(role);
        setRoleName(role.role_name);
        setCustomDataState('Edit');
    }

    const handleclearRoleName = async () => {
        setRoleName('');
        setSelectedRole(null);
        setSelectedRoleEdit(null);
        setCustomDataState('Add');
    }

    useEffect(() => {
        if (!selectedRoleEdit) return;

        const hasChanges = selectedRoleEdit?.role_name !== roleName;
        setRoleUpdateHasChanges(hasChanges);
    }, [selectedRoleEdit, roleName]);

    const handleDeleteRole = (role: Role) => {
        if (role) {
            handleclearRoleName();
            confirmDialog({
                message: (
                    <div className="text-center d-flex justify-content-center flex-column">
                        <span>Are you sure you want to delete this <b>role</b>?</span>
                        <span>This action will <b>delete all</b> records associated with it.</span>
                    </div>
                ),
                header: "Confirm the deletion",
                icon: "bi bi-info-circle",
                defaultFocus: "accept",
                acceptClassName: "p-button-danger",
                accept: () => deleteRole(role?.id!),
                dismissableMask: true,
                resizable: false,
                draggable: false,
            });
        }
    }

    const deleteRole = async (roleId: number) => {
        if (!roleId) return;
        //  loading id tigger 
        setRoleDeleteLoadingId(roleId);

        const result = await roleDeleteAPI(roleId);
        if (result.statusCode === 200) {
            roleDataGet();
            setRoleDeleteLoadingId(null);
            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Role deleted successfully.",
                    life: 3000,
                });
            }
        } else {
            setRoleDeleteLoadingId(null);
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error deleting the role.",
                    life: 3000,
                });

            }
        }

    }


    return (
        <Dialog
            visible={isOpen}
            headerClassName="d-none"
            dismissableMask
            className="custom_modal_dialog modal_dialog_sm data_select_modal"
            contentClassName="data_select_modal_content"
            onHide={onClose}
        >
            <div className="custom_modal_body data_select_modal_area">
                <div className="data_select_modal_header">
                    <DataListModalSearch
                        key={`searchKey`}
                        inputId="searchKey"
                        value={searchKey}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchKey(e.target.value)}
                        placeholder="Search role by name"
                        disabled={roleList?.length === 0}
                        onClear={() => setSearchKey("")}
                    />

                    <DataListModalClose
                        onClose={onClose}
                    />
                </div>

                {loadingRole ? (
                    <CustomTableLoader />
                ) : errorRole ? (
                    <div className="data_select_modal_body has_custom_input">
                        <DataException
                            message={errorRole}
                        />
                    </div>
                ) : (
                    <div className="data_select_modal_body has_custom_input">
                        {filterRoleList && filterRoleList?.length > 0 ? (
                            <div className="data_select_option_area">
                                {filterRoleList?.map((role) => (
                                    <div
                                        key={`role_${role?.id}`}
                                        className={`data_select_option p-ripple ${role?.id === selectedRole?.id ? "selected" : ""}
                                        ${role.data_type === "User data" ? 'custom' : ''}`}
                                        onClick={() => handleSelectRole(role)

                                        }
                                    >
                                        <span>{role?.role_name}</span>
                                        <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />

                                        {role.data_type === "User data" && (
                                            <div className="custom_data_action_area">
                                                {canEdit && (
                                                    <>
                                                        <button className="is_btn data_select_option_edit_btn p-ripple"
                                                            onClick={(e) => {
                                                                e.stopPropagation();

                                                                if (selectedRoleEdit !== null) {
                                                                    handleclearRoleName();

                                                                    setSelectedRoleEdit(null);
                                                                } else {
                                                                    handleEditRole(role);
                                                                }
                                                            }}>
                                                            <i className={`bi ${(selectedRoleEdit !== null && selectedRoleEdit?.id === role?.id) ? 'bi-x-lg' : 'bi-pencil-square'}`}></i>
                                                        </button>

                                                        <button className="is_btn data_select_option_edit_btn delete p-ripple"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteRole(role);
                                                            }}
                                                            disabled={roleDeleteLoadingId === role?.id}
                                                        >
                                                            <i className={roleDeleteLoadingId === role?.id ? 'ri-loader-4-fill loading_spinner' : 'bi bi-trash'}></i>
                                                        </button>
                                                    </>
                                                )}

                                                {/* {canEdit && (
                                                    <button className="is_btn data_select_option_edit_btn delete p-ripple"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            // handleDeleteCustomFSTileShop(shop);
                                                        }}
                                                        disabled={deleteTileShopLoadingId === shop?.id}
                                                    >
                                                        <i className={deleteTileShopLoadingId === shop?.id ? 'ri-loader-4-fill loading_spinner' : 'bi bi-trash'}></i>
                                                    </button>
                                                )} */}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <DataException
                                message={searchKey ? 'The shop you were looking for is not found!' :
                                    `No shops found. ${canEdit ? 'To add the shop click on add button.' : ''}`}
                            />
                        )}
                    </div>
                )}

                {canEdit && (
                    <div className="page_form_group data_select_modal_footer mb-0">
                        <div className="input_with_button_area">
                            <InputText
                                key={`roleName`}
                                id="roleName"
                                value={roleName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRoleName(e.target.value)}
                                className="custom_form_input has_add_value custom_data_input"
                                placeholder="Enter shop name"
                                autoComplete="off"
                            />
                            <button
                                className="add_value_btn fill p-ripple"
                                onClick={customDataState === 'Add' ? handleCreateRole : handleUpdateRole}
                                disabled={(loadingAddRole || roleName === "") || (customDataState === 'Edit' && !roleUpdateHasChanges)}
                            >
                                <i className={loadingAddRole ? "ri-loader-4-fill loading_spinner" : customDataState === 'Edit' ? "bi bi-pencil-square" : "bi bi-plus-lg"}></i>
                                <span>{loadingAddRole ? "Processing" : customDataState === 'Edit' ? "Update" : "Add"}</span>
                                <Ripple />
                            </button>
                        </div>
                        {roleNameError && (
                            <small className="form_error_msg">
                                {roleNameError}
                            </small>
                        )}
                    </div>
                )}
            </div>
        </Dialog>
    )
}


export default RoleSelectionModal;